import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import LabeledInput from '../LabeledInput';
import { GroupInputs } from '../GroupInputs';
import { SelectInput } from '../SelectInput';
import { MultiEnum } from '../Question/MultiEnum';
import { getOptionsFromRecord, getOptionsIntersection } from '../utils';

import * as api from '../../api';
import * as logger from '../../logger';

interface Props {
  value: string;
  updateFieldValue: (value: string) => void;
  saveFieldValue: (value: string) => void;
  contraceptives: api.PatientSocialHistory.Contraceptive[];
}

export const SexualHealth: React.FC<Props> = ({
  value,
  updateFieldValue,
  saveFieldValue,
  contraceptives,
}) => {
  const { t } = useTranslation();
  const [sexualHealth, setSexualHealth] = useState<
    Partial<api.PatientSocialHistory.SexualHealth>
  >({});

  const sexualHealthUpdate = (update: {
    [key in keyof api.PatientSocialHistory.SexualHealth]?:
      | string
      | boolean
      | string[];
  }) => {
    if (update.active === false) {
      return JSON.stringify(update);
    }
    return JSON.stringify({ ...sexualHealth, ...update });
  };

  const saveFormField = (value: string) => {
    updateFieldValue(value);
    saveFieldValue(value);
  };

  useEffect(() => {
    try {
      if (!value) return;
      const parsedValue: api.PatientSocialHistory.SexualHealth =
        JSON.parse(value);
      setSexualHealth(parsedValue);
    } catch (e) {
      logger.error(e, 'Error parsing sexual health value');
    }
  }, [value]);

  if (!value) {
    saveFormField(sexualHealthUpdate({ active: false }));
  }

  const sexualOrientationValues = getOptionsFromRecord(
    api.Previsit.sexualOrientationMap,
    t,
  );
  const activeValues = getOptionsFromRecord({ false: 'No', true: 'Yes' }, t);
  const contraceptiveOptions = getOptionsIntersection(
    api.PatientSocialHistory.Contraceptive,
    contraceptives,
  ).map((contraceptive) => {
    return {
      display: t(api.Previsit.contraceptiveMap[contraceptive]),
      key: contraceptive,
      value: contraceptive,
    };
  });

  return (
    <GroupInputs>
      <LabeledInput
        label={t('Are you sexually active?')}
        htmlFor="sexual-health-active"
      >
        <SelectInput
          id="sexual-health-active"
          value={String(sexualHealth.active) || ''}
          onChange={(newValue: string) => {
            saveFormField(sexualHealthUpdate({ active: newValue === 'true' }));
          }}
          options={activeValues}
        />
      </LabeledInput>
      {sexualHealth.active === true && (
        <>
          <LabeledInput
            label={t('What is your sexual orientation?')}
            htmlFor="sexual-health-orientation"
          >
            <SelectInput
              id="sexual-health-orientation"
              value={sexualHealth.orientation || ''}
              onChange={(newValue: string) => {
                saveFormField(sexualHealthUpdate({ orientation: newValue }));
              }}
              options={sexualOrientationValues}
            />
          </LabeledInput>
          <LabeledInput
            label={t(
              'Birth control / contraceptive used (select all that apply)',
            )}
          >
            <MultiEnum
              values={
                sexualHealth.contraceptives
                  ? sexualHealth.contraceptives.map(
                      (contraceptive) => contraceptive,
                    )
                  : []
              }
              options={contraceptiveOptions}
              smallRowMargin={true}
              onItemClick={(contraceptive) => {
                let { contraceptives } = sexualHealth;
                if (contraceptives?.includes(contraceptive)) {
                  sexualHealth.contraceptives = contraceptives.filter(
                    (cont) => cont !== contraceptive,
                  );
                } else if (contraceptives) {
                  contraceptives.push(contraceptive);
                } else {
                  contraceptives = [contraceptive];
                }
                saveFormField(sexualHealthUpdate({ contraceptives }));
              }}
            />
          </LabeledInput>
        </>
      )}
    </GroupInputs>
  );
};
